/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { When } from "react-if";
import { KeyValue } from "~/generated/graphql";
import { KeyValueList } from "~/components/KeyValueList/KeyValueList";
import { getTextFromStringify } from "~/tools";
import { paramsExcludedFromBaselinePatching } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/constant";

type BaselineDefinedSettingsProps = {
  isEntityBaselineDependent: boolean;
  parameters: KeyValue[];
  renderValue?: (value: string) => React.ReactNode;
};

export function BaselineDefinedSettings({
  isEntityBaselineDependent,
  parameters,
  renderValue = (value) => getTextFromStringify(value) || "—",
}: BaselineDefinedSettingsProps) {
  const { baselineParams } = parameters?.reduce<{
    baselineParams: KeyValue[];
    independentParams: KeyValue[];
  }>(
    (acc, param) => {
      const isBaselineParam =
        isEntityBaselineDependent &&
        !paramsExcludedFromBaselinePatching.includes(param.key);

      return {
        baselineParams: isBaselineParam
          ? [...acc.baselineParams, param]
          : acc.baselineParams,
        independentParams: !isBaselineParam
          ? [...acc.independentParams, param]
          : acc.independentParams,
      };
    },
    { baselineParams: [], independentParams: [] }
  ) ?? { baselineParams: [], independentParams: [] };

  return (
    <When condition={baselineParams.length > 0}>
      <KeyValueList
        title="Settings defined by Baseline source"
        items={baselineParams}
        renderValue={renderValue}
      />
    </When>
  );
}
